import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { haulAPI } from "@/lib/api";
import { getVerdictColor, formatPrice } from "@/lib/utils-app";
import {
  MapPin, CalendarBlank, Receipt, Storefront, CaretDown,
  Trash, PencilSimple, ArrowsLeftRight, Plus, Spinner, X,
  CurrencyDollar, Package, ArrowRight, GitMerge, Check, Warning,
} from "@phosphor-icons/react";
import BottomNav from "@/components/app/BottomNav";
import FinanceTabs from "@/components/app/FinanceTabs";
import { useConfirm } from "@/components/app/ConfirmDialog";

export default function HaulTrackerPage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [hauls, setHauls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [expandedHaul, setExpandedHaul] = useState(null);
  const [editingCost, setEditingCost] = useState(null);
  const [costValue, setCostValue] = useState("");
  const [scanningReceipt, setScanningReceipt] = useState(null);
  const [renamingHaul, setRenamingHaul] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [mergeSource, setMergeSource] = useState(null);
  const [reassignItem, setReassignItem] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationValue, setLocationValue] = useState("");
  const [savingLocation, setSavingLocation] = useState(false);
  const receiptRef = useRef(null);

  const loadHauls = useCallback(async () => {
    try {
      const [haulsRes, statsRes] = await Promise.all([
        haulAPI.list(),
        haulAPI.stats(),
      ]);
      setHauls(haulsRes.data);
      setStats(statsRes.data);
    } catch {} finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadHauls(); }, [loadHauls]);

  const handleDelete = async (haulId) => {
    const ok = await confirm({
      title: "Delete this haul?",
      message: "The haul record will be removed. Items themselves won't be deleted.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await haulAPI.delete(haulId);
      setHauls((prev) => prev.filter((h) => h.id !== haulId));
      if (expandedHaul === haulId) setExpandedHaul(null);
    } catch {}
  };

  const handleSaveCost = async (haulId) => {
    const cost = parseFloat(costValue);
    if (isNaN(cost)) return;
    try {
      await haulAPI.update(haulId, { total_cost: cost });
      await loadHauls();
    } catch {}
    setEditingCost(null);
  };

  const handleRename = async (haulId) => {
    if (!renameValue.trim()) return;
    try {
      await haulAPI.update(haulId, { name: renameValue.trim() });
      setHauls((prev) => prev.map((h) => h.id === haulId ? { ...h, name: renameValue.trim() } : h));
    } catch {}
    setRenamingHaul(null);
  };

  const handleMerge = async (targetId) => {
    if (!mergeSource) return;
    try {
      await haulAPI.merge({ source_haul_id: mergeSource, target_haul_id: targetId });
      await loadHauls();
    } catch {}
    setMergeSource(null);
  };

  const handleReassign = async (targetHaulId) => {
    if (!reassignItem) return;
    try {
      await haulAPI.reassign({ verdict_id: reassignItem, target_haul_id: targetHaulId });
      await loadHauls();
    } catch {}
    setReassignItem(null);
  };

  const handleReceiptFile = async (e, haulId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanningReceipt(haulId);
    try {
      const b64 = await fileToBase64(file);
      await haulAPI.scanReceipt({ image_base64: b64, haul_id: haulId });
      await loadHauls();
    } catch {}
    setScanningReceipt(null);
    if (receiptRef.current) receiptRef.current.value = "";
  };

  const handleDeleteReceipt = async (haulId, receiptId) => {
    const ok = await confirm({
      title: "Delete this receipt?",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await haulAPI.deleteReceipt(haulId, receiptId);
      await loadHauls();
    } catch {}
  };

  const handleSaveLocation = async (haulId) => {
    if (!locationValue.trim()) return;
    setSavingLocation(true);
    try {
      await haulAPI.update(haulId, { address: locationValue.trim() });
      await loadHauls();
    } catch {}
    setSavingLocation(false);
    setEditingLocation(null);
  };

  const handleCreate = async (name) => {
    try {
      await haulAPI.create({ name, date: new Date().toISOString().split("T")[0] });
      await loadHauls();
    } catch {}
    setShowCreateModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Spinner size={32} weight="bold" className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Header */}
      {/* Tabs */}
      <div className="sticky top-0 z-50 bg-white">
        <FinanceTabs />
        <div className="flex items-center justify-between px-6 py-3 border-b border-neutral-200">
          <h1 className="font-heading text-xl font-black tracking-tighter uppercase">Hauls</h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="p-2 bg-black text-white border-2 border-black active:translate-y-[1px] transition-transform"
            data-testid="create-haul-btn"
            title="Create haul manually"
          >
            <Plus size={18} weight="bold" />
          </button>
        </div>
      </div>

      {/* Stats bar */}
      {stats && stats.total_hauls > 0 && (
        <div className="grid grid-cols-3 border-b-2 border-black">
          <StatBox label="Hauls" value={stats.total_hauls} />
          <StatBox label="Items" value={stats.total_items} border />
          <StatBox label="Spent" value={`$${stats.total_cost.toFixed(0)}`} />
        </div>
      )}

      {/* Merge/Reassign mode banner */}
      {mergeSource && (
        <div className="p-3 bg-yellow-100 border-b-2 border-black flex items-center justify-between">
          <span className="font-body text-xs font-semibold">
            <GitMerge size={12} weight="bold" className="inline mr-1" />
            Select a haul to merge into
          </span>
          <button onClick={() => setMergeSource(null)} className="p-1"><X size={14} weight="bold" /></button>
        </div>
      )}
      {reassignItem && (
        <div className="p-3 bg-blue-100 border-b-2 border-black flex items-center justify-between">
          <span className="font-body text-xs font-semibold">
            <ArrowsLeftRight size={12} weight="bold" className="inline mr-1" />
            Select a haul to move item to
          </span>
          <button onClick={() => setReassignItem(null)} className="p-1"><X size={14} weight="bold" /></button>
        </div>
      )}

      {/* Haul list */}
      {hauls.length === 0 ? (
        <div className="p-8 text-center" data-testid="hauls-empty">
          <Package size={48} weight="bold" className="mx-auto text-neutral-300 mb-4" />
          <div className="font-heading text-xl font-bold uppercase mb-2">No Hauls Yet</div>
          <p className="font-body text-sm text-neutral-500 mb-4">
            Hauls are created automatically when you scan items.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-black text-white font-heading font-bold uppercase text-sm border-2 border-black"
          >
            Create Haul Manually
          </button>
        </div>
      ) : (
        <div>
          {hauls.map((haul) => {
            const isVirtual = haul.is_virtual;
            const isMergeTarget = mergeSource && mergeSource !== haul.id && !isVirtual;
            const isReassignTarget = reassignItem && !isVirtual;

            return (
              <div key={haul.id} className={`border-b-2 border-black transition-opacity duration-200 ${
                expandedHaul && expandedHaul !== haul.id && !mergeSource && !reassignItem ? "opacity-40" : "opacity-100"
              }`} data-testid={`haul-${haul.id}`}>
                {/* Haul header */}
                <button
                  onClick={() => {
                    if (isMergeTarget) { handleMerge(haul.id); return; }
                    if (isReassignTarget) { handleReassign(haul.id); return; }
                    setExpandedHaul(expandedHaul === haul.id ? null : haul.id);
                  }}
                  className={`w-full p-4 text-left flex items-center gap-3 transition-colors ${
                    isMergeTarget ? "hover:bg-yellow-50 bg-yellow-50/50" :
                    isReassignTarget ? "hover:bg-blue-50 bg-blue-50/50" :
                    "hover:bg-neutral-50"
                  }`}
                >
                  <div className={`w-10 h-10 flex items-center justify-center shrink-0 ${
                    isMergeTarget ? "bg-yellow-500 text-white" :
                    isReassignTarget ? "bg-blue-500 text-white" :
                    isVirtual ? "bg-amber-500 text-white" :
                    "bg-black text-white"
                  }`}>
                    {isMergeTarget ? <GitMerge size={20} weight="bold" /> :
                     isReassignTarget ? <ArrowRight size={20} weight="bold" /> :
                     isVirtual ? <Warning size={20} weight="bold" /> :
                     <MapPin size={20} weight="bold" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    {renamingHaul === haul.id ? (
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="text"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleRename(haul.id)}
                          className="flex-1 p-1 border-2 border-black font-heading text-sm font-bold"
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                        <button onClick={(e) => { e.stopPropagation(); handleRename(haul.id); }} className="p-1 bg-black text-white">
                          <Check size={14} weight="bold" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setRenamingHaul(null); }} className="p-1 border border-black">
                          <X size={14} weight="bold" />
                        </button>
                      </div>
                    ) : (
                      <div className="font-heading text-sm font-bold tracking-tight truncate">{haul.name}</div>
                    )}
                    <div className="font-body text-xs text-neutral-500 flex items-center gap-2 mt-0.5">
                      <span className="flex items-center gap-0.5">
                        <CalendarBlank size={10} weight="bold" /> {haul.date}
                      </span>
                      <span>{haul.item_count || 0} item{(haul.item_count || 0) !== 1 ? "s" : ""}</span>
                      {haul.total_cost > 0 && (
                        <span className="flex items-center gap-0.5">
                          <CurrencyDollar size={10} weight="bold" /> ${haul.total_cost.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                  {!isMergeTarget && !isReassignTarget && (
                    <div className="shrink-0 flex items-center gap-2">
                      {haul.cost_per_item > 0 && (
                        <div className="text-right">
                          <div className="font-heading text-sm font-bold">${haul.cost_per_item.toFixed(2)}</div>
                          <div className="font-body text-[10px] text-neutral-500">per item</div>
                        </div>
                      )}
                      <CaretDown size={14} weight="bold" className={`transition-transform ${expandedHaul === haul.id ? "rotate-180" : ""}`} />
                    </div>
                  )}
                  {(isMergeTarget || isReassignTarget) && (
                    <span className="font-heading text-xs font-bold uppercase text-neutral-400">Tap to select</span>
                  )}
                </button>

                {/* Expanded content */}
                {expandedHaul === haul.id && !mergeSource && !reassignItem && (
                  <div className="border-t-2 border-black">
                    {/* Actions row — hidden for virtual unassigned haul */}
                    {!isVirtual && (
                    <div className="grid grid-cols-6 border-b border-neutral-200">
                      {editingCost === haul.id ? (
                        <div className="col-span-6 flex items-center gap-2 p-2">
                          <span className="font-body text-xs font-semibold">$</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={costValue}
                            onChange={(e) => setCostValue(e.target.value)}
                            className="flex-1 p-1.5 border-2 border-black font-body text-sm"
                            placeholder="Total cost"
                            autoFocus
                          />
                          <button onClick={() => handleSaveCost(haul.id)} className="p-1.5 bg-black text-white font-heading text-xs font-bold uppercase">Save</button>
                          <button onClick={() => setEditingCost(null)} className="p-1.5 border border-black font-heading text-xs font-bold uppercase">Cancel</button>
                        </div>
                      ) : editingLocation === haul.id ? (
                        <div className="col-span-6 flex items-center gap-2 p-2">
                          <MapPin size={14} weight="bold" className="shrink-0" />
                          <input
                            type="text"
                            value={locationValue}
                            onChange={(e) => setLocationValue(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSaveLocation(haul.id)}
                            className="flex-1 p-1.5 border-2 border-black font-body text-sm"
                            placeholder="Store address (e.g. 123 Main St, City, ST)"
                            autoFocus
                          />
                          <button
                            onClick={() => handleSaveLocation(haul.id)}
                            disabled={savingLocation}
                            className="p-1.5 bg-black text-white font-heading text-xs font-bold uppercase disabled:opacity-50"
                          >
                            {savingLocation ? "..." : "Save"}
                          </button>
                          <button onClick={() => setEditingLocation(null)} className="p-1.5 border border-black font-heading text-xs font-bold uppercase">Cancel</button>
                        </div>
                      ) : (
                        <>
                          <ActionBtn
                            icon={<CurrencyDollar size={12} weight="bold" />}
                            label={haul.total_cost > 0 ? `$${haul.total_cost.toFixed(0)}` : "Cost"}
                            onClick={() => { setEditingCost(haul.id); setCostValue(haul.total_cost?.toString() || ""); }}
                          />
                          <ActionBtn
                            icon={scanningReceipt === haul.id ? <Spinner size={12} className="animate-spin" /> : <Receipt size={12} weight="bold" />}
                            label={scanningReceipt === haul.id ? "..." : "Receipt"}
                            onClick={() => receiptRef.current?.click()}
                            disabled={scanningReceipt === haul.id}
                          />
                          <ActionBtn
                            icon={<MapPin size={12} weight="bold" />}
                            label="Location"
                            onClick={() => { setEditingLocation(haul.id); setLocationValue(haul.address || ""); }}
                          />
                          <ActionBtn
                            icon={<PencilSimple size={12} weight="bold" />}
                            label="Rename"
                            onClick={() => { setRenamingHaul(haul.id); setRenameValue(haul.name); }}
                          />
                          <ActionBtn
                            icon={<GitMerge size={12} weight="bold" />}
                            label="Merge"
                            onClick={() => setMergeSource(haul.id)}
                            disabled={hauls.length < 2}
                          />
                          <ActionBtn
                            icon={<Trash size={12} weight="bold" />}
                            label="Delete"
                            onClick={() => handleDelete(haul.id)}
                            danger
                          />
                        </>
                      )}
                    </div>
                    )}

                    {/* Mileage & Location info */}
                    {!isVirtual && (haul.address || haul.mileage?.round_trip_miles) && (
                      <div className="px-3 py-2 border-b border-neutral-200 bg-neutral-50 font-body text-xs text-neutral-600 flex items-center gap-3 flex-wrap">
                        {haul.address && (
                          <span className="flex items-center gap-1">
                            <MapPin size={10} weight="bold" /> {haul.address.length > 50 ? haul.address.slice(0, 50) + "..." : haul.address}
                          </span>
                        )}
                        {haul.mileage?.round_trip_miles > 0 && (
                          <span className="font-semibold">
                            {haul.mileage.round_trip_miles} mi round trip
                          </span>
                        )}
                      </div>
                    )}

                    {!isVirtual && (
                    <input
                      ref={receiptRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => handleReceiptFile(e, haul.id)}
                    />
                    )}

                    {/* Unassigned hint */}
                    {isVirtual && (
                      <div className="p-3 bg-amber-50 border-b border-amber-200 font-body text-xs text-amber-700">
                        <ArrowsLeftRight size={12} weight="bold" className="inline mr-1" />
                        Tap the arrow icon on each item to move it to a haul
                      </div>
                    )}

                    {/* Items list */}
                    {(haul.item_details || []).length > 0 ? (
                      <div>
                        {(haul.item_details || []).map((item) => {
                          const colors = getVerdictColor(item.verdict);
                          const isListed = !!item.ebay_listing_id;
                          return (
                            <div key={item.id} className="flex items-center gap-2 p-3 border-b border-neutral-100 last:border-0">
                              {item.thumbnail ? (
                                <div className="w-10 h-10 border border-black shrink-0 overflow-hidden relative">
                                  <img src={`data:image/jpeg;base64,${item.thumbnail}`} alt="" className="w-full h-full object-cover" />
                                  <div
                                    className="absolute bottom-0 right-0 w-3.5 h-3.5 flex items-center justify-center text-[7px] font-black"
                                    style={{ backgroundColor: colors.bg, color: colors.text }}
                                  >
                                    {item.verdict?.[0]}
                                  </div>
                                </div>
                              ) : (
                                <div className="w-10 h-10 border border-black flex items-center justify-center text-xs font-black shrink-0"
                                  style={{ backgroundColor: colors.bg, color: colors.text }}>
                                  {item.verdict?.[0]}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="font-body text-xs font-semibold truncate flex items-center gap-1">
                                  {item.title_keywords}
                                  {isListed && (
                                    <span className="inline-flex items-center bg-[#002FA7] text-white px-1 py-0.5 text-[8px] font-bold uppercase shrink-0">
                                      <Storefront size={7} weight="fill" className="mr-0.5" /> eBay
                                    </span>
                                  )}
                                </div>
                                <div className="font-body text-[10px] text-neutral-500">{item.brand}</div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <div className="font-heading text-xs font-bold">
                                  {formatPrice(item.list_price_low, item.list_price_high)}
                                </div>
                                <button
                                  onClick={() => setReassignItem(item.id)}
                                  className="p-1 text-neutral-400 hover:text-black"
                                  title="Move to another haul"
                                >
                                  <ArrowsLeftRight size={14} weight="bold" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-4 text-center font-body text-xs text-neutral-400">
                        No items in this haul yet
                      </div>
                    )}

                    {/* Receipts section - supports multiple */}
                    {((haul.receipts && haul.receipts.length > 0) || haul.receipt_photo) && (
                      <div className="p-3 border-t border-neutral-200">
                        <label className="font-heading text-xs font-bold uppercase tracking-wider text-neutral-500 flex items-center gap-1 mb-2">
                          <Receipt size={12} weight="bold" /> Receipts
                          {haul.receipts && haul.receipts.length > 1 && (
                            <span className="ml-1 text-neutral-400">({haul.receipts.length})</span>
                          )}
                        </label>
                        {/* Multiple receipts */}
                        {haul.receipts && haul.receipts.length > 0 ? (
                          <div className="space-y-2">
                            {haul.receipts.map((receipt) => (
                              <div key={receipt.id} className="border-2 border-black overflow-hidden relative group">
                                {receipt.path && (
                                  <img
                                    src={`/api/images/file/${receipt.path}`}
                                    alt="Receipt"
                                    className="w-full object-contain max-h-52"
                                    loading="lazy"
                                  />
                                )}
                                <div className="flex items-center justify-between p-2 bg-neutral-50 border-t border-black">
                                  <div className="font-body text-xs">
                                    {receipt.total > 0 && (
                                      <span className="font-bold">${receipt.total.toFixed(2)}</span>
                                    )}
                                    {receipt.store_name && (
                                      <span className="text-neutral-500 ml-2">{receipt.store_name}</span>
                                    )}
                                    {receipt.date && (
                                      <span className="text-neutral-400 ml-2">{receipt.date}</span>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => handleDeleteReceipt(haul.id, receipt.id)}
                                    className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                    title="Delete receipt"
                                    data-testid={`delete-receipt-${receipt.id}`}
                                  >
                                    <Trash size={14} weight="bold" />
                                  </button>
                                </div>
                              </div>
                            ))}
                            {haul.receipts.length > 1 && (
                              <div className="text-right font-heading text-xs font-bold text-neutral-600 pt-1">
                                Total: ${(haul.receipts.reduce((sum, r) => sum + (r.total || 0), 0)).toFixed(2)}
                              </div>
                            )}
                          </div>
                        ) : haul.receipt_photo ? (
                          /* Legacy single receipt fallback */
                          <div className="border-2 border-black overflow-hidden">
                            <img
                              src={`/api/images/file/${haul.receipt_photo}`}
                              alt="Receipt"
                              className="w-full object-contain max-h-64"
                              loading="lazy"
                            />
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Haul Modal */}
      {showCreateModal && <CreateHaulModal onClose={() => setShowCreateModal(false)} onCreate={handleCreate} />}

      <BottomNav />
    </div>
  );
}

function StatBox({ label, value, border }) {
  return (
    <div className={`p-3 text-center ${border ? "border-x-2 border-black" : ""}`}>
      <div className="font-heading text-lg font-black">{value}</div>
      <div className="font-body text-xs text-neutral-500 uppercase">{label}</div>
    </div>
  );
}

function ActionBtn({ icon, label, onClick, disabled, danger }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`p-2 flex flex-col items-center justify-center gap-0.5 font-body text-[10px] font-semibold uppercase transition-colors disabled:opacity-30 ${
        danger ? "text-red-500 hover:bg-red-50" : "hover:bg-neutral-50"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function CreateHaulModal({ onClose, onCreate }) {
  const [name, setName] = useState("");

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center pb-[72px] sm:pb-0">
      <div className="bg-white border-t-4 sm:border-4 border-black w-full sm:max-w-sm sm:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
        <div className="flex items-center justify-between p-4 border-b-2 border-black">
          <h2 className="font-heading text-base font-black uppercase">Create Haul</h2>
          <button onClick={onClose} className="p-1"><X size={18} weight="bold" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="font-body text-xs font-semibold text-neutral-700">Haul Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Goodwill Bins Downtown"
              className="w-full mt-1 p-2 border-2 border-black font-body text-sm"
              autoFocus
              data-testid="create-haul-name"
            />
          </div>
          <button
            onClick={() => onCreate(name || `Haul • ${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`)}
            className="w-full p-3 bg-black text-white font-heading font-bold uppercase text-sm border-2 border-black"
            data-testid="create-haul-submit"
          >
            Create Haul
          </button>
        </div>
      </div>
    </div>
  );
}

function fileToBase64(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
}
